/* ---------------------------------------------------------------------------
   Speed optimization (Aug 2026): defer offscreen video downloads.

   Baseline measurement on the homepage showed 66 <video> elements, of which
   ~46 begin downloading during initial page load even though most are far
   below the fold. That competes with the hero image, fonts and product
   images for bandwidth and slows down first paint / LCP, especially on
   mobile connections.

   What this does:
   - On DOMContentLoaded, any video further than 1.5 viewport-heights from
     the screen (or currently hidden, e.g. inactive slider slides) has its
     src/<source> temporarily detached and load() called, which ABORTS the
     in-flight download. The element itself stays in place - no visual
     change, the poster image still shows.
   - TWO independent mechanisms bring a deferred video back BEFORE the
     visitor reaches it (whichever fires first wins):
       1. an IntersectionObserver with a 150% viewport margin;
       2. a lightweight interval fallback (~1s) that checks positions and
          restores anything within 1.5 viewport-heights - covers browsers/
          states where observer callbacks are delayed. The interval stops
          itself permanently once every video has been restored.
   - On restore the sources are re-attached, load() is called, and if the
     video was an autoplay video play() is requested - so playback looks
     identical to before.
   - Videos near the viewport at load (hero, first sections, PDP gallery)
     are never touched and keep 100% native behavior.
   - Videos added later (quick view, drawers) are not managed.

   Net effect: identical visuals/playback, but only videos the visitor can
   (nearly) see consume bandwidth. Fully reversible by clearing this file.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  function init() {
    var vids = Array.prototype.slice.call(document.querySelectorAll('video'));
    if (!vids.length) return;

    var store = new WeakMap();
    var deferredList = [];

    function strip(v) {
      if (store.has(v)) return;
      var sources = Array.prototype.slice.call(v.querySelectorAll('source'));
      var srcAttr = v.getAttribute('src');
      if (!srcAttr && sources.length === 0) return; /* nothing to defer */
      store.set(v, {
        src: srcAttr,
        sources: sources,
        wasAutoplay: v.autoplay || v.hasAttribute('autoplay') || !v.paused
      });
      deferredList.push(v);
      try { v.pause(); } catch (e) { /* noop */ }
      if (srcAttr) v.removeAttribute('src');
      sources.forEach(function (s) { v.removeChild(s); });
      try { v.load(); } catch (e) { /* aborts in-flight download */ }
      v.setAttribute('data-perf-deferred', '');
    }

    function restore(v) {
      var data = store.get(v);
      if (!data) return;
      store.delete(v);
      var i = deferredList.indexOf(v);
      if (i !== -1) deferredList.splice(i, 1);
      data.sources.forEach(function (s) { v.appendChild(s); });
      if (data.src) v.setAttribute('src', data.src);
      try { v.load(); } catch (e) { /* noop */ }
      v.removeAttribute('data-perf-deferred');
      if (data.wasAutoplay) {
        var p = v.play();
        if (p && typeof p.catch === 'function') p.catch(function () { /* autoplay policy */ });
      }
    }

    function isNear(v) {
      var r = v.getBoundingClientRect();
      var vh = window.innerHeight || 800;
      /* hidden 0x0 elements are only restored by the IntersectionObserver
         (they become intersecting the moment their slider shows them) */
      if (r.width === 0 && r.height === 0) return false;
      return r.top < vh * 2.5 && r.bottom > -(vh * 1.5);
    }

    var io = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) restore(en.target);
        });
      }, { rootMargin: '150% 0px 150% 0px' });
    }

    var vh = window.innerHeight || 800;
    vids.forEach(function (v) {
      var r = v.getBoundingClientRect();
      var hidden = r.width === 0 && r.height === 0;
      var far = r.top > vh * 1.5 || r.bottom < -(vh * 1.5);
      if (far || hidden) strip(v);
      if (io) io.observe(v);
    });

    /* fallback poller - restores by position even if observer is delayed */
    if (deferredList.length) {
      var timer = setInterval(function () {
        if (!deferredList.length) { clearInterval(timer); return; }
        deferredList.slice().forEach(function (v) {
          if (isNear(v)) restore(v);
        });
      }, 1000);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
